import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ChevronLeft, Save, LogOut, User, Search, 
  Loader2, LayoutDashboard, Camera, X
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function UserProfilePage() {
  const navigate = useNavigate();
  const { user, logout, checkAuth } = useAuth();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    city: '',
    picture: '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        phone: user.phone || '',
        city: user.city || '',
        picture: user.picture || '',
      });
    }
  }, [user]);

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(t('profile.photoInvalidType') || 'Format invalide. Utilisez JPEG, PNG ou WebP.');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('profile.photoTooLarge') || 'Photo trop volumineuse. Maximum 5MB.');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => {
      setPhotoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    // Upload the photo
    setUploadingPhoto(true);
    try {
      const uploadFormData = new FormData();
      uploadFormData.append('file', file);

      const response = await fetch(`${API_URL}/api/upload/coach-photo`, {
        method: 'POST',
        body: uploadFormData,
      });

      if (response.ok) {
        const data = await response.json();
        // Construct full URL for the photo
        const photoUrl = `${API_URL}${data.url}`;
        setFormData(prev => ({ ...prev, picture: photoUrl }));
        toast.success(t('profile.photoUploaded') || 'Photo téléchargée !');
      } else {
        toast.error(t('profile.photoUploadError') || 'Erreur lors du téléchargement');
        setPhotoPreview(null);
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(t('profile.photoUploadError') || 'Erreur lors du téléchargement');
      setPhotoPreview(null);
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleRemovePhoto = () => {
    setPhotoPreview(null);
    setFormData(prev => ({ ...prev, picture: '' }));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/users/profile`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        toast.success(t('profile.toastSaved'));
        await checkAuth();
      } else {
        toast.error(t('profile.toastSaveError'));
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(t('profile.toastSaveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard" className="text-muted-foreground hover:text-foreground">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/">
                <img src="/logo.png" alt="Motivya" className="h-8 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center gap-3">
              <Link to="/explore">
                <Button variant="ghost" size="sm">
                  <Search className="w-4 h-4" />
                </Button>
              </Link>
              <Link to="/dashboard">
                <Button variant="ghost" size="sm">
                  <LayoutDashboard className="w-4 h-4" />
                </Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={handleLogout}>
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-2 uppercase" data-testid="profile-page-title">{t('profile.title')}</h1>
        <p className="text-muted-foreground mb-8">{t('profile.subtitle')}</p>

        <div className="space-y-6">
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-6 mb-6">
              {/* Profile Photo with Upload */}
              <div className="relative group">
                <div className="w-24 h-24 rounded-full bg-muted flex items-center justify-center overflow-hidden border-2 border-border">
                  {uploadingPhoto ? (
                    <Loader2 className="w-8 h-8 text-primary animate-spin" />
                  ) : photoPreview || formData.picture ? (
                    <img 
                      src={photoPreview || formData.picture} 
                      alt={user?.name} 
                      className="w-full h-full object-cover" 
                    />
                  ) : (
                    <User className="w-12 h-12 text-muted-foreground" />
                  )}
                </div>
                
                {/* Upload/Change Button Overlay */}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center cursor-pointer"
                  data-testid="change-photo-btn"
                >
                  <Camera className="w-6 h-6 text-white" />
                </button>
                
                {/* Remove Photo Button */}
                {(photoPreview || formData.picture) && !uploadingPhoto && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-destructive text-white flex items-center justify-center hover:bg-destructive/80 transition-colors"
                    data-testid="remove-photo-btn"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
                
                {/* Hidden File Input */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePhotoSelect}
                  className="hidden"
                  data-testid="photo-input"
                />
              </div>
              
              <div className="flex-1">
                <h2 className="text-xl font-bold">{user?.name}</h2>
                <p className="text-muted-foreground">{user?.email}</p>
                <span className="inline-block mt-1 px-2 py-0.5 bg-primary/10 text-primary text-xs rounded">
                  {user?.role === 'coach' ? t('profile.coach') : t('profile.practitioner')}
                </span>
                <p className="text-xs text-muted-foreground mt-2">
                  {t('profile.clickToChangePhoto') || 'Cliquez sur la photo pour la modifier'}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg p-6 space-y-4">
            <h3 className="font-bold uppercase">{t('profile.info')}</h3>
            
            <div className="space-y-2">
              <Label htmlFor="name">{t('profile.fullName')}</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                data-testid="user-profile-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">{t('profile.phone')}</Label>
              <Input
                id="phone"
                value={formData.phone}
                onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+32 xxx xx xx xx"
                data-testid="user-profile-phone"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="city">{t('profile.city')}</Label>
              <Input
                id="city"
                value={formData.city}
                onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                placeholder={t('profile.cityPlaceholder')}
                data-testid="user-profile-city"
              />
            </div>
          </div>

          {user?.role !== 'coach' && (
            <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="font-bold uppercase mb-2">{t('profile.becomeCoach')}</h3>
              <p className="text-muted-foreground text-sm mb-4">{t('profile.becomeCoachDesc')}</p>
              <Link to="/register?role=coach">
                <Button className="btn-secondary">{t('profile.learnMore')}</Button>
              </Link>
            </div>
          )}

          <div className="flex justify-end">
            <Button
              onClick={handleSave}
              className="btn-primary"
              disabled={saving}
              data-testid="save-user-profile"
            >
              {saving ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  {t('profile.save')}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
